import React, { useState, useCallback, useEffect } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import {
  Mail, MessageCircle, Send, CheckCircle, AlertCircle,
  User, MapPin, Phone, Plane
} from 'lucide-react';

const WHATSAPP_PHONE = '2348068079807';

const createWhatsAppURL = (data) => {
  const message = `Hello Oscar Global,

*Name:* ${data.name.trim()}
*Email:* ${data.email.trim() || '—'}

*Inquiry:*
${data.message.trim()}

_Sent via Oscar Global Web_`;

  return `https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent(message)}`;
};

const safelyOpenWhatsApp = (url) => {
  try {
    const win = window.open(url, '_blank', 'noopener,noreferrer,width=540,height=800');
    if (win) {
      win.focus();
      return true;
    }
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.click();
    return true;
  } catch {
    return false;
  }
};

const Contact = ({ isDarkMode = false }) => {
  const reduceMotion = useReducedMotion();
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');

  useEffect(() => {
    const prevBg = document.body.style.backgroundColor;
    const prevScheme = document.body.style.colorScheme;
    document.body.style.backgroundColor = isDarkMode ? '#061110' : '#F7FBFA';
    document.body.style.colorScheme = isDarkMode ? 'dark' : 'light';
    return () => {
      document.body.style.backgroundColor = prevBg;
      document.body.style.colorScheme = prevScheme;
    };
  }, [isDarkMode]);

  const validate = useCallback(() => {
    const errs = {};
    if (!formData.name.trim()) errs.name = 'Full name is required';
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errs.email = 'Please enter a valid email address';
    }
    if (!formData.message.trim()) errs.message = 'Please describe your travel needs';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [formData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    setStatus('submitting');
    const url = createWhatsAppURL(formData);
    const success = safelyOpenWhatsApp(url);

    if (success) {
      setStatus('success');
      setFormData({ name: '', email: '', message: '' });
      setErrors({});
      setTimeout(() => setStatus('idle'), 4000);
    } else {
      setStatus('error');
    }
  };

  const heroReveal = reduceMotion ? {} : {
    initial: { opacity: 0, y: 26 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] }
  };

  const cardReveal = reduceMotion ? {} : {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }
  };

  const globalStyles = `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Sora:wght@600;700&display=swap');

    .contact-root {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      background: var(--bg);
      color: var(--text);
      min-height: 100vh;
      -webkit-font-smoothing: antialiased;
    }
    .contact-root * { box-sizing: border-box; }
    .contact-root h1, .contact-root h2, .contact-root h3 { font-family: 'Sora', sans-serif; color: var(--text); margin: 0; }
    .contact-root p { color: var(--text-muted); margin: 0; }
    
    .contact-root :focus-visible {
      outline: 3px solid var(--focus);
      outline-offset: 2px;
      border-radius: 8px;
    }

    .container { width: min(1180px, calc(100% - 2.5rem)); margin-inline: auto; }
    .section { padding-block: clamp(4rem, 8vw, 6.75rem); }

    .hero {
      padding-top: clamp(8rem, 12vw, 10rem);
      text-align: center;
      margin-bottom: 2rem;
    }
    .sectiona{
      margin-top: 4rem;
    }

    .eyebrow {
      display: inline-flex; align-items: center; gap: 0.55rem;
      padding: 0.48rem 0.85rem; border-radius: 999px;
      border: 1px solid var(--border-strong); background: var(--surface);
      color: var(--primary); font-size: 0.78rem; font-weight: 700;
      letter-spacing: 0.14em; text-transform: uppercase;
    }

    .h1 {
      font-size: clamp(2.5rem, 5vw, 3.75rem); line-height: 1.1;
      letter-spacing: -0.03em; margin: 1.25rem 0 1.5rem; font-weight: 700;
    }

    .lead {
      font-size: 1.125rem; line-height: 1.7; color: var(--text-muted);
      max-width: 640px; margin: 0 auto;
    }

    .contact-grid {
      display: grid;
      grid-template-columns: 1fr;
      gap: 2rem;
    }
    @media (min-width: 900px) {
      .contact-grid { grid-template-columns: 1fr 1.2fr; gap: 3rem; align-items: start; }
    }

    .info-card, .form-card {
      background: var(--surface);
      border: 1px solid var(--border);
      border-radius: 24px;
      padding: clamp(2rem, 4vw, 2.5rem);
      box-shadow: var(--shadow);
      backdrop-filter: blur(14px);
    }
    
    .info-card h2 { font-size: 1.5rem; margin-bottom: 1rem; }
    .info-card > p { margin-bottom: 2rem; line-height: 1.7; }
    
    .info-item {
      display: flex; align-items: flex-start; gap: 1rem;
      margin-bottom: 1.5rem; font-size: 0.95rem; color: var(--text); line-height: 1.6;
    }
    .info-item:last-child { margin-bottom: 0; }
    .info-item svg { color: var(--primary); flex-shrink: 0; margin-top: 2px; }
    .info-item a { color: inherit; text-decoration: none; transition: color 0.2s; }
    .info-item a:hover { color: var(--primary); }

    .form-grid { display: flex; flex-direction: column; gap: 1.5rem; }
    .form-group { display: flex; flex-direction: column; gap: 0.5rem; }
    
    .form-label {
      font-size: 0.875rem; font-weight: 600; color: var(--text);
      display: flex; align-items: center; gap: 0.5rem;
    }
    .form-label .req { color: var(--accent); }

    .form-input, .form-textarea {
      width: 100%; padding: 0.875rem 1rem; border-radius: 14px;
      border: 1px solid var(--border-strong); background: var(--bg-muted);
      color: var(--text); font-size: 0.95rem; font-family: inherit;
      transition: all 0.2s ease; outline: none;
    }
    .form-input:focus, .form-textarea:focus {
      border-color: var(--primary); box-shadow: 0 0 0 4px var(--focus); background: var(--surface);
    }
    .form-input.error, .form-textarea.error {
      border-color: #ef4444; box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.12);
    }
    .form-textarea { resize: vertical; min-height: 140px; }

    .form-error {
      font-size: 0.8rem; color: #ef4444;
      display: flex; align-items: center; gap: 0.35rem; margin-top: -0.25rem;
    }

    .btn-submit {
      width: 100%; padding: 1rem; border-radius: 999px;
      background: linear-gradient(135deg, var(--btn-from), var(--btn-to));
      color: #fff; font-size: 1rem; font-weight: 700; border: none; cursor: pointer;
      display: flex; align-items: center; justify-content: center; gap: 0.6rem;
      box-shadow: 0 14px 32px rgba(15, 118, 110, 0.28);
      transition: all 0.25s ease; margin-top: 0.5rem; font-family: inherit;
    }
    .btn-submit:hover:not(:disabled) {
      transform: translateY(-2px); box-shadow: 0 18px 38px rgba(15, 118, 110, 0.36);
    }
    .btn-submit:disabled { opacity: 0.7; cursor: not-allowed; }

    .status-msg {
      padding: 1rem 1.25rem; border-radius: 16px;
      display: flex; align-items: flex-start; gap: 0.75rem;
      margin-bottom: 1.5rem; font-size: 0.9rem; font-weight: 500; line-height: 1.5;
    }
    .status-msg.success {
      background: rgba(16, 185, 129, 0.08); border: 1px solid rgba(16, 185, 129, 0.25); color: #10b981;
    }
    .status-msg.error {
      background: rgba(239, 68, 68, 0.08); border: 1px solid rgba(239, 68, 68, 0.25); color: #ef4444;
    }
    .status-msg a { color: inherit; text-decoration: underline; font-weight: 600; }

    @keyframes spin { to { transform: rotate(360deg); } }
    .spinner {
      width: 18px; height: 18px;
      border: 2px solid rgba(255,255,255,0.3); border-top-color: #fff;
      border-radius: 50%; animation: spin 0.8s linear infinite;
    }

    @media (max-width: 640px) { .container { width: calc(100% - 2rem); } }
  `;

  return (
    <main
      className="contact-root"
      style={{
        '--bg': isDarkMode ? '#061110' : '#F7FBFA',
        '--bg-muted': isDarkMode ? '#0A1917' : '#EFF7F5',
        '--surface': isDarkMode ? 'rgba(16, 38, 35, 0.72)' : '#FFFFFF',
        '--border': isDarkMode ? 'rgba(94, 234, 212, 0.14)' : 'rgba(15, 118, 110, 0.14)',
        '--border-strong': isDarkMode ? 'rgba(94, 234, 212, 0.26)' : 'rgba(15, 118, 110, 0.26)',
        '--text': isDarkMode ? '#E9F6F3' : '#132E29',
        '--text-muted': isDarkMode ? '#A8C5BE' : '#4A645E',
        '--primary': isDarkMode ? '#2DD4BF' : '#0F766E',
        '--btn-from': isDarkMode ? '#0D9488' : '#0F766E',
        '--btn-to': isDarkMode ? '#0F766E' : '#134E4A',
        '--accent': isDarkMode ? '#F59E0B' : '#B45309',
        '--focus': isDarkMode ? 'rgba(94, 234, 212, 0.25)' : 'rgba(15, 118, 110, 0.15)',
        '--shadow': isDarkMode ? '0 18px 50px rgba(0, 0, 0, 0.42)' : '0 18px 50px rgba(15, 118, 110, 0.10)'
      }}
    >
      <style>{globalStyles}</style>

      <section className="hero sectiona">
        <div className="container">
          <motion.div {...heroReveal}>
            <span className="eyebrow">
              <Plane size={14} aria-hidden="true" /> Get in Touch
            </span>
            <h1 className="h1">Let's Plan Your Journey</h1>
            <p className="lead">
              Have a question or ready to book? Send us a message and our team 
              will get back to you shortly.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section" style={{ paddingTop: 0 }}>
        <div className="container contact-grid">
          
          <motion.div className="info-card" {...cardReveal}>
            <h2>Reach Out Directly</h2>
            <p>Prefer to talk or visit? Use the details below to connect with us.</p>
            
            <div className="info-item">
              <MapPin size={20} />
              <span>M3 Los Angeles Mall,<br />Ahmadu Bello Way, Mabushi,<br />Abuja, Nigeria</span>
            </div>
            
            <div className="info-item">
              <Phone size={20} />
              <a href={`tel:+${WHATSAPP_PHONE}`}>+234 806 807 9807</a>
            </div>
            
            <div className="info-item">
              <Mail size={20} />
              <a href="mailto:oscarglobaltt@gmail.com">oscarglobaltt@gmail.com</a>
            </div>
          </motion.div>

          <motion.div className="form-card" {...cardReveal}>
            {status === 'success' && (
              <div className="status-msg success" role="alert">
                <CheckCircle size={20} style={{ flexShrink: 0, marginTop: '2px' }} />
                <div>
                  <strong>Message prepared!</strong><br />
                  WhatsApp is open with your details. Tap <strong>Send</strong> to complete.
                </div>
              </div>
            )}

            {status === 'error' && (
              <div className="status-msg error" role="alert">
                <AlertCircle size={20} style={{ flexShrink: 0, marginTop: '2px' }} />
                <div>
                  <strong>Couldn't open WhatsApp.</strong><br />
                  Please ensure pop-ups are allowed or <a href={`https://wa.me/${WHATSAPP_PHONE}`} target="_blank" rel="noopener noreferrer">click here to open manually</a>.
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="form-grid" noValidate>
              <div className="form-group">
                <label htmlFor="contact-name" className="form-label">
                  <User size={16} /> Full Name <span className="req">*</span>
                </label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g. Adaobi Chukwu"
                  className={`form-input ${errors.name ? 'error' : ''}`}
                />
                {errors.name && <span className="form-error"><AlertCircle size={14} /> {errors.name}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="contact-email" className="form-label">
                  <Mail size={16} /> Email
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="adaobi@example.com"
                  className={`form-input ${errors.email ? 'error' : ''}`}
                />
                {errors.email && <span className="form-error"><AlertCircle size={14} /> {errors.email}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="contact-message" className="form-label">
                  <MessageCircle size={16} /> Message <span className="req">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your travel plans, preferred dates, and any special requests..."
                  className={`form-textarea ${errors.message ? 'error' : ''}`}
                />
                {errors.message && <span className="form-error"><AlertCircle size={14} /> {errors.message}</span>}
              </div>

              <button
                type="submit"
                disabled={status === 'submitting'}
                className="btn-submit"
              >
                {status === 'submitting' ? (
                  <><span className="spinner" /> Opening WhatsApp…</>
                ) : (
                  <><Send size={18} /> Send via WhatsApp</>
                )}
              </button>
            </form>
          </motion.div>

        </div>
      </section>
    </main>
  );
};

export default Contact;































// import React from 'react'
// import '../styles/contact.css'

// import {MdOutlineEmail} from 'react-icons/md'
// import {RiMessengerLine} from 'react-icons/ri'
// import {BsWhatsapp} from 'react-icons/bs'
// import { useRef } from 'react';
// import emailjs from 'emailjs-com'

// const Contact = () => {
//   const form = useRef();

  
//   const sendEmail = (e) => {
//     e.preventDefault();
   

//     emailjs.sendForm('service_szsl3o9', 'template_npgmgkc', form.current, 'MVOnBm93e5wWUolMo');
//     e.target.reset()
//   };
//   return (
//     <section id='contact' className='contact__sec'>
//         {/* <h5>Get In Touch</h5> */}
//         <h2 className='special' style={{ fontFamily: "'Island Moments', cursive" }}>Contact Us</h2>
//         <div className="container contact__container">
//           <div className="contact__options">
//            <article className='contact__option' data-aos='fade-up'>
//              <MdOutlineEmail className='contact__option-icon'/>
//              <h4>Email</h4>
//              <h5>oscarglobaltt@gmail.com</h5>
//              <a href="mailto:oscarglobaltt@gmail.com" target={'_blank'} rel="noopener">Send a message</a>
//            </article>
//            <article className='contact__option' data-aos='fade-up'>
//              <RiMessengerLine className='contact__option-icon'/>
//              <h4>Messenger</h4>
//              <h5>Oscar Global Intl</h5>
//              <a href="" target={'_blank'} rel="noopener">Send a message</a>
//            </article>
//            <article className='contact__option' data-aos='fade-up'>
//              <BsWhatsapp className='contact__option-icon'/>
//              <h4>WhatsApp</h4>
//              <h5>+234(0)8077854009</h5>
//              <a href={'https://api.whatsapp.com/send?phone=+2348077854009'} target={'_blank'} rel="noopener">Send a message</a>
//            </article>
//           </div>
//           {/* END OF CONTACT OPTION */}
//           <form ref={form} onSubmit={sendEmail} data-aos='fade-up'>
//             <input type="text" name="name" placeholder='Your Full Name' required />
//             <input type="email" name="email" placeholder='Your Email' required />
//             <textarea name='message' rows="7" required></textarea>
//             <button type='submit' className='btn btn-primary'>Send Message</button>
//           </form>
//         </div>
//       </section>
//   )
// }

// export default Contact